import _ from "lodash";
import { SessionStore } from "../uidispatcherapi/SessionStore";

import FetchArticlesForHomepagePreviewAction from "../actions/FetchArticlesForHomepagePreviewAction";
import { Store } from "../utils/Store";
import ArticleStore from "./ArticleStore";
import { StoreListItem } from "../datatypes/StoreList";

type ItemsIdsType = {
    teaserIds: string[];
    nonTeaserIds: string[];
};
let idsMap: {
    [keys: string]: ItemsIdsType;
} = {};
let determindedTeaserMap: {
    [keys: string]: any;
} = {};
let requestedIds: {
    [keys: string]: Array<string>;
} = {};
let currentStoreId: string | undefined | null = null;
let currenCustomerId: string | undefined | null = null;

const MAX_NUMBER_OF_ARTICLES_TO_SHOW_LANDING_PAGE = 6;
const CHUNK_SIZE = 12;

class ArticlesLandingPageStoreImpl extends Store {
    constructor() {
        super();

        this.listenTo(FetchArticlesForHomepagePreviewAction, this.fetchArticlesForPreview);
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this.listenTo(ArticleStore, this.onArticleStoreTrigger);
        if (!SessionStore.pending()) {
            this.onSessionStoreTrigger();
        }
    }

    onSessionStoreTrigger() {
        const storeId = SessionStore.getCustomerStore();
        const customerId = SessionStore.getCustomerId();
        if ((storeId && storeId !== currentStoreId) || (customerId && customerId !== currenCustomerId)) {
            determindedTeaserMap = {};
            idsMap = {};
            requestedIds = {};
            currentStoreId = storeId;
            currenCustomerId = customerId;
        }
    }

    determineTeaserHomepage(
        listId: string,
        articleMap: {
            [keys: string]: any;
        }
    ): Array<any> {
        const articleIdsWithData = _.chain(articleMap)
            .filter(item => item && !item.state)
            .map(item => item.variantId.bettyVariantId)
            .value();
        const teaserIdsWithData = _.intersection(articleIdsWithData, idsMap[listId].teaserIds);
        const nonTeaserIdsWithData = _.intersection(articleIdsWithData, idsMap[listId].nonTeaserIds);

        if (_.size(teaserIdsWithData) >= MAX_NUMBER_OF_ARTICLES_TO_SHOW_LANDING_PAGE) {
            return _.chain(teaserIdsWithData)
                .shuffle()
                .take(MAX_NUMBER_OF_ARTICLES_TO_SHOW_LANDING_PAGE)
                .map(id => articleMap[id])
                .value();
        }
        return _.map(
            _.union(
                teaserIdsWithData,
                _.take(
                    _.shuffle(nonTeaserIdsWithData),
                    MAX_NUMBER_OF_ARTICLES_TO_SHOW_LANDING_PAGE - _.size(teaserIdsWithData)
                )
            ),
            id => articleMap[id]
        );
    }

    onArticleStoreTrigger(articleMap: { [keys: string]: any }) {
        const listIdsWithoutDeterminedTeaser = _.difference(_.keys(idsMap), _.keys(determindedTeaserMap));
        _.forEach(listIdsWithoutDeterminedTeaser, listId => {
            const idsArticleDataMissing = _.filter(
                requestedIds[listId],
                articleId =>
                    !articleMap[articleId] ||
                    articleMap[articleId].state === "requested" ||
                    articleMap[articleId].state === "loading"
            );
            if (_.size(idsArticleDataMissing) === 0) {
                this.reactOnAllRequestedDataLoaded(listId, articleMap);
            }
        });
    }

    reactOnAllRequestedDataLoaded(
        listId: string,
        articleMap: {
            [keys: string]: any;
        }
    ) {
        const idsArticleDataExist = _.filter(requestedIds[listId], articleId => !articleMap[articleId].state);
        const allRequestedArticlesHaveData = _.size(requestedIds[listId]) === _.size(idsArticleDataExist);
        const enoughArticlesWithData = _.size(idsArticleDataExist) >= MAX_NUMBER_OF_ARTICLES_TO_SHOW_LANDING_PAGE;
        const articleDataForAllArticleIdsRequested =
            _.size(requestedIds[listId]) === _.size(idsMap[listId].nonTeaserIds) + _.size(idsMap[listId].teaserIds);
        if (enoughArticlesWithData || articleDataForAllArticleIdsRequested || allRequestedArticlesHaveData) {
            determindedTeaserMap[listId] = this.determineTeaserHomepage(listId, articleMap);
            this.trigger();
        } else {
            const nonFetchedTeaser = _.difference(idsMap[listId].teaserIds, requestedIds[listId]);
            const nonFetchedNonTeaser = _.difference(idsMap[listId].nonTeaserIds, requestedIds[listId]);
            const newIdsToFetch = this.determineIdsToFetch(nonFetchedNonTeaser, nonFetchedTeaser, CHUNK_SIZE);
            requestedIds[listId] = _.union(requestedIds[listId], newIdsToFetch);
            if (!_.isEmpty(newIdsToFetch)) {
                ArticleStore.fetchArticle(newIdsToFetch);
            }
        }
    }

    fetchArticlesForPreview(items: StoreListItem[], listId: string) {
        if (requestedIds[listId]) {
            return;
        }

        const teaserIds = _.map(
            _.filter(items, item => item.isHomepageTeaser),
            item => item.bettyVariantId
        );
        const nonTeaserIds = _.map(
            _.reject(items, item => item.isHomepageTeaser),
            item => item.bettyVariantId
        );
        idsMap[listId] = { nonTeaserIds: nonTeaserIds, teaserIds: teaserIds };

        this.fetchArticlesWithoutArticleSearch(listId);
    }

    fetchArticlesWithoutArticleSearch(listId: string) {
        const idsToFetch = this.determineIdsToFetch(idsMap[listId].nonTeaserIds, idsMap[listId].teaserIds, CHUNK_SIZE);
        requestedIds[listId] = idsToFetch;
        if (!_.isEmpty(idsToFetch)) {
            ArticleStore.fetchArticle(idsToFetch);
        }
    }

    determineIdsToFetch(nonTeaserIds: string[], teaserIds: string[], sizeToFetch: number): string[] {
        const amountTeaser = _.size(teaserIds);
        if (amountTeaser < sizeToFetch) {
            return _.union(teaserIds, _.take(_.shuffle(nonTeaserIds), sizeToFetch - amountTeaser));
        } else {
            return _.take(_.shuffle(teaserIds), sizeToFetch);
        }
    }

    getTeaser(listId: string) {
        return determindedTeaserMap[listId];
    }

    getNoTeaserDataExists() {
        return _.every(determindedTeaserMap, list => _.isEmpty(list));
    }

    //for testing
    _reset() {
        determindedTeaserMap = {};
        idsMap = {};
        requestedIds = {};
    }

    getIdsMapEntry(listId: string) {
        return idsMap[listId];
    }

    getRequestedIdsMapEntry(listId: string) {
        return requestedIds[listId];
    }

    setIdsMap(map: { [x: string]: any }) {
        idsMap = map;
    }

    setRequestedIdsMap(map: { [x: string]: any }) {
        requestedIds = map;
    }

    setDeterminedTeaserMap(map: { [x: string]: any }) {
        determindedTeaserMap = map;
    }

    setCustomerId(id: string) {
        currenCustomerId = id;
    }

    setStoreId(id: string) {
        currentStoreId = id;
    }
}

export const ArticlesLandingPageStore = new ArticlesLandingPageStoreImpl();
